<template>
  <div>
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>退款记录</span>
        <div class="header-actions">
          <el-button :loading="loading" @click="loadData">刷新</el-button>
        </div>
      </div>

      <el-alert
        v-if="query.groupBuyRecordId"
        class="state-block"
        type="info"
        :closable="false"
        show-icon
        title="当前正在查看某个拼团记录的退款"
      >
        <template slot>
          <span>拼团编号：{{ query.groupBuyRecordId }}</span>
          <el-button type="text" @click="clearRecordFilter">清除筛选</el-button>
        </template>
      </el-alert>

      <el-form :inline="true" :model="query" class="query-form">
        <el-form-item>
          <el-input v-model.trim="query.groupBuyRecordId" placeholder="拼团编号" clearable />
        </el-form-item>
        <el-form-item>
          <el-input v-model.trim="query.orderNo" placeholder="订单号" clearable />
        </el-form-item>
        <el-form-item>
          <el-select v-model="query.refundStatus" placeholder="退款状态" clearable>
            <el-option :value="0" label="待处理" />
            <el-option :value="1" label="处理中" />
            <el-option :value="2" label="退款成功" />
            <el-option :value="3" label="退款失败" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button @click="resetQuery">重置</el-button>
          <el-button type="primary" @click="loadData">查询</el-button>
        </el-form-item>
      </el-form>

      <el-alert
        v-if="loadError"
        class="state-block"
        title="退款记录加载失败"
        type="error"
        :closable="false"
        show-icon
      >
        <template slot>
          <span>可以稍后重试，或先检查后端服务状态。</span>
          <el-button type="text" @click="loadData">重新加载</el-button>
        </template>
      </el-alert>

      <template v-else>
        <el-empty v-if="!loading && !list.length" description="当前没有退款记录" />

        <template v-else>
          <el-table :data="list" border v-loading="loading">
            <el-table-column prop="refundNo" label="退款单号" min-width="180" />
            <el-table-column label="订单 / 拼团" min-width="220">
              <template slot-scope="{ row }">
                <div>{{ row.orderNo }}</div>
                <div class="sub-text">拼团 #{{ row.groupBuyRecordId || '-' }}</div>
              </template>
            </el-table-column>
            <el-table-column label="活动 / 商品" min-width="220">
              <template slot-scope="{ row }">
                <div>{{ row.activityTitle || '-' }}</div>
                <div class="sub-text">{{ row.productName || '-' }}</div>
              </template>
            </el-table-column>
            <el-table-column label="退款状态" width="120">
              <template slot-scope="{ row }">
                <el-tag :type="statusType(row.refundStatus)">{{ statusText(row.refundStatus) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="退款金额" width="120">
              <template slot-scope="{ row }">
                ¥{{ formatPrice(row.refundAmount) }}
              </template>
            </el-table-column>
            <el-table-column label="退款原因" min-width="180">
              <template slot-scope="{ row }">
                <span>{{ row.reason || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="时间信息" min-width="220">
              <template slot-scope="{ row }">
                <div>创建：{{ formatDateTime(row.createTime) }}</div>
                <div class="sub-text">回调：{{ formatDateTime(row.callbackTime) }}</div>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="260">
              <template slot-scope="{ row }">
                <el-button
                  v-if="row.refundStatus === 0"
                  type="text"
                  :loading="actionId === row.id"
                  @click="processRefund(row)"
                >
                  发起退款
                </el-button>
                <el-button
                  v-if="row.refundStatus === 0 || row.refundStatus === 1"
                  type="text"
                  :loading="actionId === row.id"
                  @click="markSuccess(row)"
                >
                  标记成功
                </el-button>
                <el-button
                  v-if="row.refundStatus === 0 || row.refundStatus === 1"
                  type="text"
                  :loading="actionId === row.id"
                  @click="markFail(row)"
                >
                  标记失败
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <div class="table-footer">
            <div class="table-footer__meta">共 {{ total }} 条</div>
            <el-pagination
              background
              layout="prev, pager, next, sizes"
              :current-page="query.pageNum"
              :page-sizes="[10, 20, 50]"
              :page-size="query.pageSize"
              :total="total"
              @current-change="changePage"
              @size-change="changePageSize"
            />
          </div>
        </template>
      </template>
    </el-card>
  </div>
</template>

<script>
import {
  getRefundRecordPage,
  markRefundFail,
  markRefundSuccess,
  processRefundRecord
} from '../../api/admin'
import { formatDateTime, formatPrice } from '../../utils/format'

export default {
  data() {
    return {
      query: {
        groupBuyRecordId: '',
        orderNo: '',
        refundStatus: '',
        pageNum: 1,
        pageSize: 10
      },
      list: [],
      total: 0,
      loading: false,
      loadError: false,
      actionId: null
    }
  },
  created() {
    this.restoreQuery()
    this.loadData()
  },
  methods: {
    formatDateTime,
    formatPrice,
    statusText(status) {
      const map = {
        0: '待处理',
        1: '处理中',
        2: '退款成功',
        3: '退款失败'
      }
      return map[status] || '未知状态'
    },
    statusType(status) {
      const map = {
        0: 'warning',
        1: '',
        2: 'success',
        3: 'danger'
      }
      return map[status] || 'info'
    },
    restoreQuery() {
      const routeQuery = this.$route.query.q
      if (!routeQuery) return
      try {
        const parsed = JSON.parse(routeQuery)
        this.query = { ...this.query, ...parsed }
      } catch (error) {
        // ignore invalid route query
      }
    },
    async loadData() {
      this.loading = true
      this.loadError = false
      try {
        const data = await getRefundRecordPage(this.query)
        this.list = data.list || []
        this.total = data.total || 0
      } catch (error) {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },
    resetQuery() {
      this.query = {
        groupBuyRecordId: '',
        orderNo: '',
        refundStatus: '',
        pageNum: 1,
        pageSize: 10
      }
      this.loadData()
    },
    clearRecordFilter() {
      this.query.groupBuyRecordId = ''
      this.query.pageNum = 1
      this.loadData()
    },
    changePage(page) {
      this.query.pageNum = page
      this.loadData()
    },
    changePageSize(size) {
      this.query.pageSize = size
      this.query.pageNum = 1
      this.loadData()
    },
    processRefund(row) {
      this.$confirm(`确认发起退款单 ${row.refundNo} 吗？`, '发起退款', {
        type: 'warning'
      }).then(async () => {
        this.actionId = row.id
        try {
          await processRefundRecord(row.id)
          this.$message.success('退款已进入处理队列')
          await this.loadData()
        } finally {
          this.actionId = null
        }
      }).catch(() => {})
    },
    markSuccess(row) {
      this.$prompt('可选填写备注', '标记退款成功', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputPlaceholder: '例如：线下已退款'
      }).then(async ({ value }) => {
        this.actionId = row.id
        try {
          await markRefundSuccess(row.id, { remark: value || '' })
          this.$message.success('已标记为退款成功')
          await this.loadData()
        } finally {
          this.actionId = null
        }
      }).catch(() => {})
    },
    markFail(row) {
      this.$prompt('请填写失败原因', '标记退款失败', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputPlaceholder: '例如：银行账户异常'
      }).then(async ({ value }) => {
        this.actionId = row.id
        try {
          await markRefundFail(row.id, { remark: value || '' })
          this.$message.success('已标记为退款失败')
          await this.loadData()
        } finally {
          this.actionId = null
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.query-form {
  margin-bottom: 12px;
}

.state-block {
  margin-bottom: 16px;
}

.sub-text {
  margin-top: 4px;
  color: #6b7280;
  font-size: 13px;
}

.table-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
}

.table-footer__meta {
  color: #6b7280;
  font-size: 13px;
}
</style>
